import { useState, useMemo, useEffect } from "react";
import AdminLayout from "@/components/layout-admin";
import { useAdminUsers, useCreateUser, useDeleteUser, useUpdateUser } from "@/hooks/use-resources";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Trash2, Shield, Sparkles, Briefcase, Pencil, Wrench, Eye, EyeOff, User as UserIcon, FileText, Search, X, CreditCard, IdCard, FileCheck, Building, Phone, Mail, MapPin, Calendar, Lock, ClipboardList, Settings2, Globe, ClipboardEdit, Clock, Bell, AlertCircle, CheckCircle2, Home, TrendingUp, Users, Receipt, CalendarClock, SprayCan, ChevronDown, ChevronUp } from "lucide-react";
import { FEATURE_KEYS, FEATURE_LABELS, type AdditionalContact } from "@shared/schema";
import { Checkbox } from "@/components/ui/checkbox";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "@/components/ui/collapsible";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useLocation } from "wouter";
import { UserActivateAction, UserStatusFilter } from "@/components/user-active-toggle-dialog";

const emptyForm = { username: "", password: "", role: "staff" as string, fullName: "", email: "", phone: "", requirePersonalInfo: false, requireIdUpload: false, requireW9: false, requireBankInfo: false };

const CLEANER_COLORS = [
  // Reds & Roses
  "#ef4444", "#f43f5e", "#e11d48",
  // Pinks
  "#ec4899", "#db2777",
  // Purples & Violets
  "#a855f7", "#8b5cf6", "#7c3aed",
  // Indigos
  "#6366f1", "#4f46e5",
  // Blues
  "#3b82f6", "#2563eb",
  // Sky & Cyan
  "#0ea5e9", "#06b6d4", "#0891b2",
  // Teals & Emeralds
  "#14b8a6", "#0d9488", "#10b981", "#059669",
  // Greens
  "#22c55e", "#16a34a",
  // Limes
  "#84cc16", "#65a30d",
  // Yellows & Ambers
  "#eab308", "#f59e0b", "#d97706",
  // Oranges
  "#f97316", "#ea580c", "#fb923c",
  // Neutral
  "#64748b",
];

export default function AdminUsers() {
  const { data: users = [], isLoading } = useAdminUsers();
  const createUser = useCreateUser();
  const deleteUser = useDeleteUser();
  const updateUser = useUpdateUser();
  const { toast } = useToast();
  const { user: currentUser } = useAuth();
  const qc = useQueryClient();
  const [, navigate] = useLocation();
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editAdditionalRoles, setEditAdditionalRoles] = useState<string[]>([]);
  const [editShowFinances, setEditShowFinances] = useState(false);
  const [profileViewOpen, setProfileViewOpen] = useState(false);
  const [profileViewUserId, setProfileViewUserId] = useState<number | null>(null);
  const [deleteUserId, setDeleteUserId] = useState<number | null>(null);
  const [deleteUsername, setDeleteUsername] = useState("");
  const [form, setForm] = useState(emptyForm);
  const [editForm, setEditForm] = useState({ id: 0, username: "", fullName: "", email: "", phone: "", role: "cleaner" as string, password: "", requirePersonalInfo: true, requireIdUpload: true, requireW9: false, requireBankInfo: false, color: "" });
  const [searchQuery, setSearchQuery] = useState("");
  const [filterRole, setFilterRole] = useState("all");
  const [filterStatus, setFilterStatus] = useState<"active" | "inactive" | "all">("active");
  const [revealedFields, setRevealedFields] = useState<Record<string, string>>({});
  const [revealReason, setRevealReason] = useState("");
  const [revealDialogField, setRevealDialogField] = useState<string | null>(null);
  const [showAuditLog, setShowAuditLog] = useState(false);
  const [hexColorInput, setHexColorInput] = useState("");
  const [featureUserId, setFeatureUserId] = useState<number | null>(null);
  const [featureUserName, setFeatureUserName] = useState("");
  const [userDisabledFeatures, setUserDisabledFeatures] = useState<string[]>([]);
  const [pmRulesUserId, setPmRulesUserId] = useState<number | null>(null);
  const [pmRulesUserName, setPmRulesUserName] = useState("");
  const [pmRulesForm, setPmRulesForm] = useState<{
    defaultCheckoutTime: string;
    cleaningNotes: string;
    notifyOnCheckout: boolean;
    notifyOnCleaningComplete: boolean;
    requireFobReturn: boolean;
    defaultPriority: string;
    additionalNotes: string;
  }>({ defaultCheckoutTime: "", cleaningNotes: "", notifyOnCheckout: false, notifyOnCleaningComplete: false, requireFobReturn: true, defaultPriority: "normal", additionalNotes: "" });

  const [additionalContacts, setAdditionalContacts] = useState<AdditionalContact[]>([]);
  const [contactsOpen, setContactsOpen] = useState(false);

  const isSuperAdmin = (currentUser as any)?.role === "super_admin";
  const currentUserDisabledFeatures: string[] = Array.isArray((currentUser as any)?.disabledFeatures) ? (currentUser as any).disabledFeatures : [];
  const canEditTeam = isSuperAdmin || !currentUserDisabledFeatures.includes('team_management_edit');


  const { data: auditLogs = [] } = useQuery<any[]>({
    queryKey: ["/api/admin/audit-logs", profileViewUserId],
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/admin/audit-logs?targetUserId=${profileViewUserId}&limit=50`);
      return res.json();
    },
    enabled: !!profileViewUserId && showAuditLog,
  });

  const revealMutation = useMutation({
    mutationFn: async ({ userId, field, reason }: { userId: number; field: string; reason: string }) => {
      const res = await apiRequest("POST", `/api/admin/users/${userId}/tin/reveal`, { field, reason });
      return res.json();
    },
    onSuccess: (data, vars) => {
      setRevealedFields(prev => ({ ...prev, [vars.field]: data.value || "Not available" }));
      setRevealDialogField(null);
      setRevealReason("");
      toast({ title: "Access granted", description: "Sensitive data revealed. This access has been logged." });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to reveal sensitive data.", variant: "destructive" });
    },
  });

  const saveFeaturesMutation = useMutation({
    mutationFn: async ({ userId, disabledFeatures }: { userId: number; disabledFeatures: string[] }) => {
      const res = await apiRequest("PUT", `/api/admin/users/${userId}`, { disabledFeatures });
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/admin/users"] });
      setFeatureUserId(null);
      toast({ title: "Feature access updated" });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to update feature access.", variant: "destructive" });
    },
  });

  const { data: viewProfile, isLoading: profileViewLoading } = useQuery<any>({
    queryKey: ["/api/admin/users", profileViewUserId, "profile"],
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/admin/users/${profileViewUserId}/profile`);
      return res.json();
    },
    enabled: !!profileViewUserId,
  });

  const { data: pmRulesData } = useQuery<any>({
    queryKey: ["/api/admin/users", pmRulesUserId, "pm-rules"],
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/admin/users/${pmRulesUserId}/pm-rules`);
      return res.json();
    },
    enabled: !!pmRulesUserId,
  });

  const savePmRulesMutation = useMutation({
    mutationFn: async ({ userId, rules }: { userId: number; rules: any }) => {
      const res = await apiRequest("PUT", `/api/admin/users/${userId}/pm-rules`, rules);
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/admin/users", pmRulesUserId, "pm-rules"] });
      toast({ title: "Rules & Settings saved" });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to save rules.", variant: "destructive" });
    },
  });

  const applyRulesToAllMutation = useMutation({
    mutationFn: async (userId: number) => {
      const res = await apiRequest("POST", `/api/admin/users/${userId}/pm-rules/apply-to-all`, {});
      return res.json();
    },
    onSuccess: (data) => {
      toast({ title: `Applied to all apartments`, description: `${data.applied} apartment(s) updated.` });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to apply rules.", variant: "destructive" });
    },
  });

  const saveContactsMutation = useMutation({
    mutationFn: async ({ userId, contacts }: { userId: number; contacts: AdditionalContact[] }) => {
      const res = await apiRequest("PUT", `/api/admin/users/${userId}`, { additionalContacts: contacts });
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/admin/users"] });
      toast({ title: "Additional contacts saved" });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to save contacts.", variant: "destructive" });
    },
  });

  const filteredUsers = useMemo(() => {
    return (users as any[]).filter((u: any) => {
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const match = (u.fullName || "").toLowerCase().includes(q) ||
          (u.username || "").toLowerCase().includes(q) ||
          (u.email || "").toLowerCase().includes(q) ||
          (u.phone || "").toLowerCase().includes(q);
        if (!match) return false;
      }
      if (filterRole !== "all" && u.role !== filterRole) return false;
      if (filterStatus !== "all" && (u.isActive !== false) !== (filterStatus === "active")) return false;
      return true;
    });
  }, [users, searchQuery, filterRole, filterStatus]);

  const hasActiveFilters = searchQuery || filterRole !== "all" || filterStatus !== "active";

  const impersonateMutation = useMutation({
    mutationFn: async (userId: number) => {
      const res = await apiRequest("POST", "/api/admin/impersonate", { userId });
      return res.json();
    },
    onSuccess: async (data: any) => {
      qc.clear();
      await qc.refetchQueries({ queryKey: ["/api/user"] });
      const role = data.impersonatingRole;
      if (role === "owner") navigate("/owner");
      else if (role === "cleaner") navigate("/cleaner");
      else if (role === "maintenance") navigate("/maintenance");
      else if (role === "affiliate") navigate("/affiliate");
      else if (role === "platform_admin") navigate("/platform");
      else navigate("/admin");
      toast({ title: `Viewing as ${data.fullName || data.username}` });
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const handleCreate = async () => {
    if (!form.username || !form.password) {
      toast({ title: "Error", description: "Username and password are required", variant: "destructive" });
      return;
    }
    try {
      await createUser.mutateAsync({
        username: form.username,
        password: form.password,
        role: form.role as "super_admin" | "staff" | "owner" | "cleaner" | "maintenance",
        fullName: form.fullName || undefined,
        email: form.email || undefined,
        phone: form.phone || undefined,
        requirePersonalInfo: form.requirePersonalInfo,
        requireIdUpload: form.requireIdUpload,
        requireW9: form.requireW9,
        requireBankInfo: form.requireBankInfo,
      } as any);
      setForm(emptyForm);
      setCreateOpen(false);
      toast({ title: "User created" });
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
  };

  const handleEdit = async (u: any) => {
    setEditForm({
      id: u.id,
      username: u.username || "",
      fullName: u.fullName || "",
      email: u.email || "",
      phone: u.phone || "",
      role: u.role || "cleaner",
      password: "",
      requirePersonalInfo: true,
      requireIdUpload: true,
      requireW9: false,
      requireBankInfo: false,
      color: u.color || "",
    });
    setEditAdditionalRoles([]);
    setEditShowFinances(u.showFinancesInWorkerView ?? false);
    setEditOpen(true);
    try {
      const res = await apiRequest("GET", `/api/admin/users/${u.id}/profile`);
      const profile = await res.json();
      if (profile) {
        setEditForm(p => ({
          ...p,
          requirePersonalInfo: profile.requirePersonalInfo ?? true,
          requireIdUpload: profile.requireIdUpload ?? true,
          requireW9: profile.requireW9 ?? false,
          requireBankInfo: profile.requireBankInfo ?? false,
        }));
      }
    } catch {}
    try {
      const rolesRes = await apiRequest("GET", `/api/admin/users/${u.id}/roles`);
      if (rolesRes.ok) {
        const roles = await rolesRes.json();
        setEditAdditionalRoles(Array.isArray(roles) ? roles : []);
      }
    } catch (e) {
      console.error("[admin-users] failed to load roles:", e);
    }
  };

  const handleUpdate = async () => {
    try {
      const data: any = {
        id: editForm.id,
        username: editForm.username || undefined,
        fullName: editForm.fullName || undefined,
        email: editForm.email || undefined,
        phone: editForm.phone || undefined,
        role: editForm.role as "super_admin" | "staff" | "owner" | "cleaner" | "maintenance",
        color: editForm.color || null,
      };
      if (editForm.password.trim()) {
        data.password = editForm.password;
      }
      await updateUser.mutateAsync(data);
      if (editForm.role === "cleaner" || editForm.role === "maintenance" || editForm.role === "owner" || editAdditionalRoles.includes("cleaner") || editAdditionalRoles.includes("maintenance")) {
        try {
          await apiRequest("PUT", `/api/admin/users/${editForm.id}/profile`, {
            requirePersonalInfo: editForm.requirePersonalInfo,
            requireIdUpload: editForm.requireIdUpload,
            requireW9: editForm.requireW9,
            requireBankInfo: editForm.requireBankInfo,
          });
        } catch {}
      }
      // Save additional roles and worker finances toggle
      const rolesRes = await apiRequest("PUT", `/api/admin/users/${editForm.id}/roles`, { roles: editAdditionalRoles });
      if (!rolesRes.ok) throw new Error(`Failed to save roles: ${rolesRes.status}`);
      const settingsRes = await apiRequest("PUT", `/api/admin/users/${editForm.id}/worker-settings`, { showFinancesInWorkerView: editShowFinances });
      if (!settingsRes.ok) throw new Error(`Failed to save worker settings: ${settingsRes.status}`);
      // Invalidate after roles are persisted so all pages pick up additionalRoles[]
      await qc.invalidateQueries({ queryKey: ["/api/admin/users"] });
      setEditOpen(false);
      toast({ title: "User updated" });
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
  };

  const handleDelete = async () => {
    if (!deleteUserId) return;
    if (deleteUsername === "admin") {
      toast({ title: "Error", description: "Cannot delete the admin account", variant: "destructive" });
      setDeleteUserId(null);
      return;
    }
    await deleteUser.mutateAsync(deleteUserId);
    setDeleteUserId(null);
    toast({ title: "User deleted" });
  };

  const openProfileView = (userId: number) => {
    setProfileViewUserId(userId);
    setProfileViewOpen(true);
  };

  const roleConfig: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline"; icon: any }> = {
    super_admin: { label: "Super Admin", variant: "destructive", icon: Shield },
    staff: { label: "Staff Admin", variant: "default", icon: Settings2 },
    owner: { label: "Property Manager", variant: "default", icon: Briefcase },
    cleaner: { label: "Cleaner", variant: "secondary", icon: Sparkles },
    maintenance: { label: "Maintenance", variant: "outline", icon: Wrench },
  };

  const profileUser = profileViewUserId ? (users as any[]).find((u: any) => u.id === profileViewUserId) : null;

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-display text-2xl font-bold" data-testid="text-users-title">Team Management</h1>
            <p className="text-muted-foreground">
              {users ? `${filteredUsers.length} of ${(users as any[]).length} members` : "Loading..."}
            </p>
          </div>
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            {canEditTeam && (
              <DialogTrigger asChild>
                <Button data-testid="button-add-user">
                  <Plus className="w-4 h-4 mr-2" /> Add Team Member
                </Button>
              </DialogTrigger>
            )}
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add Team Member</DialogTitle>
                <DialogDescription>Create a new account for a property manager or cleaning team member.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Role</Label>
                  <Select value={form.role} onValueChange={v => setForm(p => ({ ...p, role: v }))}>
                    <SelectTrigger data-testid="select-role">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="staff">Staff Admin</SelectItem>
                      <SelectItem value="cleaner">Cleaning Team</SelectItem>
                      <SelectItem value="maintenance">Maintenance Crew</SelectItem>
                      <SelectItem value="owner">Property Manager</SelectItem>
                      <SelectItem value="super_admin">Super Admin</SelectItem>
                    </SelectContent>
                  </Select>
                  {form.role === "staff" && (
                    <p className="text-xs text-muted-foreground mt-1.5 flex items-center gap-1">
                      <Settings2 className="w-3 h-3" />
                      Staff Admins access the admin portal with feature-gated permissions you set per user.
                    </p>
                  )}
                </div>
                <div>
                  <Label>Full Name</Label>
                  <Input value={form.fullName} onChange={e => setForm(p => ({ ...p, fullName: e.target.value }))} placeholder="Maria Garcia" data-testid="input-fullname" />
                </div>
                <div>
                  <Label>Username</Label>
                  <Input value={form.username} onChange={e => setForm(p => ({ ...p, username: e.target.value }))} placeholder="maria" data-testid="input-username" />
                </div>
                <div>
                  <Label>Password</Label>
                  <Input type="password" value={form.password} onChange={e => setForm(p => ({ ...p, password: e.target.value }))} placeholder="Enter password" data-testid="input-password" />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} placeholder="maria@example.com" data-testid="input-email" />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} placeholder="+1 305-555-0100" data-testid="input-phone" />
                </div>
                {(form.role === "cleaner" || form.role === "maintenance" || form.role === "owner") && (
                  <div className="border rounded-lg p-3 space-y-3">
                    <p className="text-sm font-medium flex items-center gap-2"><FileText className="w-4 h-4" /> Onboarding Requirements</p>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label className="text-sm font-normal">Personal Information</Label>
                        <Switch checked={form.requirePersonalInfo} onCheckedChange={v => setForm(p => ({ ...p, requirePersonalInfo: v }))} data-testid="switch-require-personal" />
                      </div>
                      <div className="flex items-center justify-between">
                        <Label className="text-sm font-normal">ID Verification</Label>
                        <Switch checked={form.requireIdUpload} onCheckedChange={v => setForm(p => ({ ...p, requireIdUpload: v }))} data-testid="switch-require-id" />
                      </div>
                      <div className="flex items-center justify-between">
                        <Label className="text-sm font-normal">W-9 Tax Form</Label>
                        <Switch checked={form.requireW9} onCheckedChange={v => setForm(p => ({ ...p, requireW9: v }))} data-testid="switch-require-w9" />
                      </div>
                      <div className="flex items-center justify-between">
                        <Label className="text-sm font-normal">Bank Information</Label>
                        <Switch checked={form.requireBankInfo} onCheckedChange={v => setForm(p => ({ ...p, requireBankInfo: v }))} data-testid="switch-require-bank" />
                      </div>
                    </div>
                  </div>
                )}
                <Button className="w-full" onClick={handleCreate} disabled={createUser.isPending} data-testid="button-create-user">
                  {createUser.isPending ? "Creating..." : "Create Account"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search by name, username, email..."
              className="pl-9"
              data-testid="input-search-users"
            />
          </div>
          <Select value={filterRole} onValueChange={setFilterRole}>
            <SelectTrigger className="w-[160px]" data-testid="filter-role">
              <SelectValue placeholder="All Roles" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Roles</SelectItem>
              <SelectItem value="super_admin">Super Admin</SelectItem>
              <SelectItem value="staff">Staff Admin</SelectItem>
              <SelectItem value="owner">Property Manager</SelectItem>
              <SelectItem value="cleaner">Cleaner</SelectItem>
              <SelectItem value="maintenance">Maintenance</SelectItem>
            </SelectContent>
          </Select>
          <UserStatusFilter value={filterStatus} onChange={setFilterStatus} />
          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={() => { setSearchQuery(""); setFilterRole("all"); setFilterStatus("active"); }} data-testid="button-clear-filters">
              <X className="w-4 h-4 mr-1" /> Clear
            </Button>
          )}
        </div>

        {isLoading ? (
          <div className="text-center py-8 text-muted-foreground">Loading...</div>
        ) : (
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead className="hidden md:table-cell">Contact</TableHead>
                    <TableHead className="hidden lg:table-cell">Onboarding</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredUsers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                        {hasActiveFilters ? "No team members match your filters" : "No team members yet"}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredUsers.map((u: any) => {
                      const config = roleConfig[u.role] ?? roleConfig["cleaner"] ?? { label: u.role, variant: "outline" as const, icon: null };
                      return (
                        <TableRow key={u.id} data-testid={`row-user-${u.id}`} className={u.isActive === false ? "opacity-60" : undefined}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {(u.role === "cleaner" || u.role === "maintenance") && u.color && (<div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: u.color }} title="Calendar color" />)}
                              <div>
                                <p className="font-medium" data-testid={`text-user-name-${u.id}`}>{u.fullName || u.username}{u.isActive === false && <span className="ml-2 text-xs font-normal text-muted-foreground" data-testid={`badge-inactive-${u.id}`}>(inactive)</span>}</p>
                                <p className="text-xs text-muted-foreground">@{u.username}</p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant={config.variant} data-testid={`badge-role-${u.id}`}>{config.label}</Badge>
                          </TableCell>
                          <TableCell className="hidden md:table-cell">
                            <div className="text-sm space-y-0.5">
                              {u.email && <p className="flex items-center gap-1 text-muted-foreground"><Mail className="w-3 h-3" />{u.email}</p>}
                              {u.phone && <p className="flex items-center gap-1 text-muted-foreground"><Phone className="w-3 h-3" />{u.phone}</p>}
                              {!u.email && !u.phone && <span className="text-muted-foreground">-</span>}
                            </div>
                          </TableCell>
                          <TableCell className="hidden lg:table-cell">
                            {u.role === "cleaner" || u.role === "maintenance" || u.role === "owner" ? (
                              <div className="flex items-center gap-1 flex-wrap">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-xs"
                                  onClick={() => openProfileView(u.id)}
                                  data-testid={`button-view-profile-${u.id}`}
                                >
                                  <FileText className="w-3 h-3 mr-1" /> View Profile
                                </Button>
                                {(u.role === "cleaner" || u.role === "maintenance") && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-xs"
                                    onClick={() => navigate(`/admin/cleaner/${u.id}`)}
                                    data-testid={`button-cleaner-dashboard-${u.id}`}
                                  >
                                    <TrendingUp className="w-3 h-3 mr-1" /> Dashboard
                                  </Button>
                                )}
                                {u.role === "owner" && (
                                  <>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-xs"
                                    onClick={() => navigate(`/admin/pm/${u.id}`)}
                                    data-testid={`button-pm-dashboard-${u.id}`}
                                  >
                                    <Home className="w-3 h-3 mr-1" /> PM Dashboard
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-xs"
                                    onClick={() => {
                                      setPmRulesUserId(u.id);
                                      setPmRulesUserName(u.fullName || u.username);
                                      setPmRulesForm({ defaultCheckoutTime: "", cleaningNotes: "", notifyOnCheckout: false, notifyOnCleaningComplete: false, requireFobReturn: true, defaultPriority: "normal", additionalNotes: "" });
                                      setAdditionalContacts(Array.isArray(u.additionalContacts) ? u.additionalContacts : []);
                                      setContactsOpen((u.additionalContacts as any[])?.length > 0);
                                    }}
                                    data-testid={`button-pm-rules-${u.id}`}
                                  >
                                    <ClipboardEdit className="w-3 h-3 mr-1" /> Rules
                                  </Button>
                                  </>
                                )}
                              </div>
                            ) : (
                              <span className="text-xs text-muted-foreground">N/A</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              {canEditTeam && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleEdit(u)}
                                  title="Edit"
                                  data-testid={`button-edit-user-${u.id}`}
                                >
                                  <Pencil className="w-4 h-4" />
                                </Button>
                              )}
                              {canEditTeam && (
                                <Button
                                  variant={u.role === "staff" ? "outline" : "ghost"}
                                  size={u.role === "staff" ? "sm" : "icon"}
                                  onClick={() => {
                                    setFeatureUserId(u.id);
                                    setFeatureUserName(u.fullName || u.username);
                                    setUserDisabledFeatures(Array.isArray((u as any).disabledFeatures) ? (u as any).disabledFeatures : []);
                                  }}
                                  title="Feature Access"
                                  data-testid={`button-features-user-${u.id}`}
                                  className={u.role === "staff" ? "gap-1.5 text-xs" : ""}
                                >
                                  <Settings2 className="w-4 h-4" />
                                  {u.role === "staff" && <span>Permissions</span>}
                                </Button>
                              )}
                              {(currentUser as any)?.role === "super_admin" && u.id !== (currentUser as any)?.id && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => impersonateMutation.mutate(u.id)}
                                  disabled={impersonateMutation.isPending}
                                  title="View As"
                                  data-testid={`button-view-as-${u.id}`}
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                              )}
                              {canEditTeam && u.username !== "admin" && <UserActivateAction user={u} currentUserId={(currentUser as any)?.id ?? null} />}
                              {canEditTeam && u.username !== "admin" && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="text-destructive"
                                  onClick={() => { setDeleteUserId(u.id); setDeleteUsername(u.username); }}
                                  title="Remove"
                                  data-testid={`button-delete-user-${u.id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}


        <Dialog open={editOpen} onOpenChange={setEditOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit User</DialogTitle>
              <DialogDescription>Update user profile and optionally change their password.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Role</Label>
                <Select value={editForm.role} onValueChange={v => setEditForm(p => ({ ...p, role: v }))}>
                  <SelectTrigger data-testid="edit-select-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="staff">Staff Admin</SelectItem>
                    <SelectItem value="cleaner">Cleaning Team</SelectItem>
                    <SelectItem value="maintenance">Maintenance Crew</SelectItem>
                    <SelectItem value="owner">Property Manager</SelectItem>
                    <SelectItem value="super_admin">Super Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Full Name</Label>
                <Input value={editForm.fullName} onChange={e => setEditForm(p => ({ ...p, fullName: e.target.value }))} placeholder="Full name" data-testid="edit-input-fullname" />
              </div>
              <div>
                <Label>Username</Label>
                <Input value={editForm.username} onChange={e => setEditForm(p => ({ ...p, username: e.target.value }))} placeholder="username" data-testid="edit-input-username" />
              </div>
              <div>
                <Label>New Password</Label>
                <Input type="password" value={editForm.password} onChange={e => setEditForm(p => ({ ...p, password: e.target.value }))} placeholder="Leave blank to keep current" data-testid="edit-input-password" />
                <p className="text-xs text-muted-foreground mt-1">Leave blank to keep the current password</p>
              </div>
              <div>
                <Label>Email</Label>
                <Input type="email" value={editForm.email} onChange={e => setEditForm(p => ({ ...p, email: e.target.value }))} placeholder="email@example.com" data-testid="edit-input-email" />
              </div>
              <div>
                <Label>Phone</Label>
                <Input value={editForm.phone} onChange={e => setEditForm(p => ({ ...p, phone: e.target.value }))} placeholder="+1 305-555-0100" data-testid="edit-input-phone" />
              </div>
              {(editForm.role === "cleaner" || editForm.role === "maintenance" || editAdditionalRoles.includes("cleaner") || editAdditionalRoles.includes("maintenance")) && (
                <div>
                  <Label className="text-sm">Calendar Color</Label>
                  <p className="text-xs text-muted-foreground mb-2">Job cards show this color when pending and assigned to this person.</p>
                  <div className="space-y-2" data-testid="color-picker">
                    <div className="flex flex-wrap gap-2">
                      {CLEANER_COLORS.map(c => (
                        <button
                          key={c}
                          type="button"
                          className={`w-7 h-7 rounded-full border-2 transition-transform hover:scale-110 ${editForm.color === c && !hexColorInput ? "border-foreground scale-110 shadow-md" : "border-transparent"}`}
                          style={{ backgroundColor: c }}
                          onClick={() => { setEditForm(p => ({ ...p, color: p.color === c ? "" : c })); setHexColorInput(""); }}
                          title={c}
                          data-testid={`color-swatch-${c.replace("#","")}`}
                        />
                      ))}
                    </div>
                    <div className="flex items-center gap-2">
                      <div
                        className="w-7 h-7 rounded-full border-2 border-muted-foreground/30 flex-shrink-0"
                        style={{ backgroundColor: /^#[0-9a-fA-F]{6}$/.test(hexColorInput) ? hexColorInput : (editForm.color || "transparent") }}
                      />
                      <input
                        type="text"
                        placeholder="#000000"
                        value={hexColorInput}
                        maxLength={7}
                        onChange={e => {
                          const raw = e.target.value;
                          setHexColorInput(raw);
                          if (/^#[0-9a-fA-F]{6}$/.test(raw)) {
                            setEditForm(p => ({ ...p, color: raw }));
                          }
                        }}
                        className={`h-7 w-28 rounded-md border px-2 text-xs font-mono bg-background outline-none focus:ring-1 focus:ring-ring ${hexColorInput && !/^#[0-9a-fA-F]{6}$/.test(hexColorInput) ? "border-red-500" : "border-input"}`}
                        data-testid="color-hex-input"
                      />
                      {hexColorInput && !/^#[0-9a-fA-F]{6}$/.test(hexColorInput) && (
                        <span className="text-xs text-red-500">Invalid hex</span>
                      )}
                      {editForm.color && (
                        <button
                          type="button"
                          className="ml-auto w-7 h-7 rounded-full border-2 border-dashed border-muted-foreground flex items-center justify-center text-muted-foreground hover:text-foreground text-xs"
                          onClick={() => { setEditForm(p => ({ ...p, color: "" })); setHexColorInput(""); }}
                          title="Clear color"
                          data-testid="color-clear"
                        >✕</button>
                      )}
                    </div>
                  </div>
                </div>
              )}
              {(editForm.role === "cleaner" || editForm.role === "maintenance" || editForm.role === "owner" || editAdditionalRoles.includes("cleaner") || editAdditionalRoles.includes("maintenance")) && (
                <div className="border rounded-lg p-3 space-y-3">
                  <p className="text-sm font-medium flex items-center gap-2"><FileText className="w-4 h-4" /> Onboarding Requirements</p>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm font-normal">Personal Information</Label>
                      <Switch checked={editForm.requirePersonalInfo} onCheckedChange={v => setEditForm(p => ({ ...p, requirePersonalInfo: v }))} data-testid="edit-switch-require-personal" />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label className="text-sm font-normal">ID Verification</Label>
                      <Switch checked={editForm.requireIdUpload} onCheckedChange={v => setEditForm(p => ({ ...p, requireIdUpload: v }))} data-testid="edit-switch-require-id" />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label className="text-sm font-normal">W-9 Tax Form</Label>
                      <Switch checked={editForm.requireW9} onCheckedChange={v => setEditForm(p => ({ ...p, requireW9: v }))} data-testid="edit-switch-require-w9" />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label className="text-sm font-normal">Bank Information</Label>
                      <Switch checked={editForm.requireBankInfo} onCheckedChange={v => setEditForm(p => ({ ...p, requireBankInfo: v }))} data-testid="edit-switch-require-bank" />
                    </div>
                  </div>
                </div>
              )}
              {/* Additional Roles */}
              {(editForm.role === "cleaner" || editForm.role === "maintenance" || editForm.role === "laundry" || editForm.role === "staff" || editForm.role === "super_admin") && (
                <div className="border rounded-lg p-3 space-y-3">
                  <p className="text-sm font-medium">Additional Worker Roles</p>
                  <p className="text-xs text-muted-foreground -mt-1">Grants access to extra tabs in the worker dashboard.</p>
                  <div className="space-y-2">
                    {(["cleaner", "maintenance", "laundry"] as const).filter(r => r !== editForm.role).map(role => (
                      <div key={role} className="flex items-center justify-between">
                        <Label className="text-sm font-normal capitalize">{role}</Label>
                        <Switch
                          checked={editAdditionalRoles.includes(role)}
                          onCheckedChange={v => setEditAdditionalRoles(prev =>
                            v ? [...prev, role] : prev.filter(r => r !== role)
                          )}
                          data-testid={`edit-switch-role-${role}`}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {/* Worker finances visibility */}
              <div className="flex items-center justify-between border rounded-lg p-3">
                <div>
                  <p className="text-sm font-medium">Show Finances in Worker View</p>
                  <p className="text-xs text-muted-foreground">Enables the Earnings tab in the worker dashboard.</p>
                </div>
                <Switch
                  checked={editShowFinances}
                  onCheckedChange={setEditShowFinances}
                  data-testid="edit-switch-show-finances"
                />
              </div>
              <Button className="w-full" onClick={handleUpdate} disabled={updateUser.isPending} data-testid="button-update-user">
                {updateUser.isPending ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={!!featureUserId} onOpenChange={(open) => { if (!open) setFeatureUserId(null); }}>
          <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Settings2 className="w-5 h-5" />
                Feature Access — {featureUserName}
              </DialogTitle>
              <DialogDescription>
                Toggle which features this user can access. Disabled features will be hidden from their sidebar.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              {FEATURE_KEYS.map((key) => {
                const isDisabled = userDisabledFeatures.includes(key);
                return (
                  <div key={key} className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-gray-50 dark:hover:bg-gray-900">
                    <Label className="text-sm font-normal cursor-pointer">{FEATURE_LABELS[key] || key}</Label>
                    <Switch
                      checked={!isDisabled}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setUserDisabledFeatures(prev => prev.filter(f => f !== key));
                        } else {
                          setUserDisabledFeatures(prev => [...prev, key]);
                        }
                      }}
                      data-testid={`feature-toggle-${key}`}
                    />
                  </div>
                );
              })}
            </div>
            <div className="flex gap-2 pt-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setFeatureUserId(null)}
              >
                Cancel
              </Button>
              <Button
                className="flex-1"
                onClick={() => {
                  if (featureUserId) {
                    saveFeaturesMutation.mutate({ userId: featureUserId, disabledFeatures: userDisabledFeatures });
                  }
                }}
                disabled={saveFeaturesMutation.isPending}
                data-testid="button-save-features"
              >
                {saveFeaturesMutation.isPending ? "Saving..." : "Save Feature Access"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={profileViewOpen} onOpenChange={(open) => { if (!open) { setProfileViewOpen(false); setProfileViewUserId(null); } }}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <UserIcon className="w-5 h-5" />
                {profileUser?.fullName || profileUser?.username || "User"} - Profile Details
              </DialogTitle>
              <DialogDescription>
                View submitted onboarding information for this team member.
              </DialogDescription>
            </DialogHeader>
            {profileViewLoading ? (
              <div className="space-y-4">
                {[1, 2, 3].map(i => <Skeleton key={i} className="h-20 w-full" />)}
              </div>
            ) : !viewProfile ? (
              <div className="text-center py-8 text-muted-foreground">
                <UserIcon className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No profile data submitted yet.</p>
                <p className="text-sm mt-1">This user hasn't completed their onboarding.</p>
              </div>
            ) : (<>
              <Tabs defaultValue="personal" className="w-full">
                <TabsList className="w-full grid grid-cols-4">
                  <TabsTrigger value="personal" data-testid="tab-personal">Personal</TabsTrigger>
                  <TabsTrigger value="id" data-testid="tab-id">ID</TabsTrigger>
                  <TabsTrigger value="w9" data-testid="tab-w9">W-9</TabsTrigger>
                  <TabsTrigger value="bank" data-testid="tab-bank">Bank</TabsTrigger>
                </TabsList>

                <TabsContent value="personal" className="space-y-4 mt-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Badge variant={viewProfile.personalInfoCompleted ? "default" : "secondary"}>
                      {viewProfile.personalInfoCompleted ? "Completed" : "Not Completed"}
                    </Badge>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <ProfileField icon={MapPin} label="Address" value={viewProfile.address} />
                    <ProfileField icon={Building} label="City" value={viewProfile.city} />
                    <ProfileField icon={MapPin} label="State" value={viewProfile.state} />
                    <ProfileField icon={MapPin} label="ZIP Code" value={viewProfile.zipCode} />
                    <ProfileField icon={Calendar} label="Date of Birth" value={viewProfile.dateOfBirth} />
                  </div>
                  {(viewProfile.emergencyContactName || viewProfile.emergencyContactPhone) && (
                    <>
                      <Separator />
                      <p className="text-sm font-medium">Emergency Contact</p>
                      <div className="grid grid-cols-2 gap-4">
                        <ProfileField icon={UserIcon} label="Name" value={viewProfile.emergencyContactName} />
                        <ProfileField icon={Phone} label="Phone" value={viewProfile.emergencyContactPhone} />
                      </div>
                    </>
                  )}
                </TabsContent>

                <TabsContent value="id" className="space-y-4 mt-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Badge variant={viewProfile.idUploadCompleted ? "default" : "secondary"}>
                      {viewProfile.idUploadCompleted ? "Verified" : "Not Submitted"}
                    </Badge>
                    {viewProfile.idType && (
                      <Badge variant="outline" className="capitalize">{viewProfile.idType}</Badge>
                    )}
                  </div>
                  {viewProfile.idFrontUrl ? (
                    <div className="space-y-3">
                      <div>
                        <p className="text-sm text-muted-foreground mb-1">ID Front</p>
                        <div className="border rounded-lg overflow-hidden bg-muted">
                          <img src={viewProfile.idFrontUrl} alt="ID Front" className="max-h-48 w-auto mx-auto object-contain" data-testid="img-id-front" />
                        </div>
                      </div>
                      {viewProfile.idBackUrl && (
                        <div>
                          <p className="text-sm text-muted-foreground mb-1">ID Back</p>
                          <div className="border rounded-lg overflow-hidden bg-muted">
                            <img src={viewProfile.idBackUrl} alt="ID Back" className="max-h-48 w-auto mx-auto object-contain" data-testid="img-id-back" />
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-4">No ID documents uploaded.</p>
                  )}
                </TabsContent>

                <TabsContent value="w9" className="space-y-4 mt-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Badge variant={viewProfile.w9Completed ? "default" : "secondary"}>
                      {viewProfile.w9Completed ? "Completed" : "Not Submitted"}
                    </Badge>
                  </div>
                  {viewProfile.w9Completed ? (
                    <div className="grid grid-cols-2 gap-4">
                      <ProfileField icon={UserIcon} label="Legal Name" value={viewProfile.w9LegalName} />
                      <ProfileField icon={Building} label="Business Name" value={viewProfile.w9BusinessName} />
                      <ProfileField icon={FileCheck} label="Tax Classification" value={viewProfile.w9TaxClassification} />
                      <div className="col-span-2 flex items-center gap-2 p-2 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded text-amber-800 dark:text-amber-200 text-xs">
                        <Lock className="h-3.5 w-3.5 shrink-0" />
                        <span>Sensitive data is encrypted. Click the eye icon to reveal (requires a reason and will be logged).</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <ProfileField icon={Lock} label="SSN" value={revealedFields.ssn || viewProfile.w9Ssn || "Not provided"} />
                        {viewProfile.w9Ssn && !revealedFields.ssn && (
                          <Button size="icon" variant="ghost" className="h-7 w-7 shrink-0" onClick={() => setRevealDialogField("ssn")} data-testid="button-reveal-ssn">
                            <Eye className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        {revealedFields.ssn && (
                          <Button size="icon" variant="ghost" className="h-7 w-7 shrink-0" onClick={() => setRevealedFields(prev => { const n = { ...prev }; delete n.ssn; return n; })} data-testid="button-hide-ssn">
                            <EyeOff className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <ProfileField icon={Lock} label="EIN" value={revealedFields.ein || viewProfile.w9Ein || "Not provided"} />
                        {viewProfile.w9Ein && !revealedFields.ein && (
                          <Button size="icon" variant="ghost" className="h-7 w-7 shrink-0" onClick={() => setRevealDialogField("ein")} data-testid="button-reveal-ein">
                            <Eye className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        {revealedFields.ein && (
                          <Button size="icon" variant="ghost" className="h-7 w-7 shrink-0" onClick={() => setRevealedFields(prev => { const n = { ...prev }; delete n.ein; return n; })} data-testid="button-hide-ein">
                            <EyeOff className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                      <ProfileField icon={MapPin} label="Address" value={viewProfile.w9Address} />
                      <ProfileField icon={Building} label="City" value={viewProfile.w9City} />
                      <ProfileField icon={MapPin} label="State" value={viewProfile.w9State} />
                      <ProfileField icon={MapPin} label="ZIP Code" value={viewProfile.w9ZipCode} />
                      <ProfileField icon={Calendar} label="Signature Date" value={viewProfile.w9SignatureDate} />
                      <ProfileField icon={FileText} label="Exempt Payee Code" value={viewProfile.w9ExemptPayeeCode} />
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-4">W-9 form not submitted yet.</p>
                  )}
                </TabsContent>

                <TabsContent value="bank" className="space-y-4 mt-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Badge variant={viewProfile.bankInfoCompleted ? "default" : "secondary"}>
                      {viewProfile.bankInfoCompleted ? "Completed" : "Not Submitted"}
                    </Badge>
                  </div>
                  {viewProfile.bankInfoCompleted ? (
                    <div className="grid grid-cols-2 gap-4">
                      <ProfileField icon={Building} label="Bank Name" value={viewProfile.bankName} />
                      <div className="flex items-center gap-2">
                        <ProfileField icon={Lock} label="Account Number" value={revealedFields.bankAccount || viewProfile.bankAccountNumber || "Not provided"} />
                        {viewProfile.bankAccountNumber && !revealedFields.bankAccount && (
                          <Button size="icon" variant="ghost" className="h-7 w-7 shrink-0" onClick={() => setRevealDialogField("bankAccount")} data-testid="button-reveal-bank-account">
                            <Eye className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        {revealedFields.bankAccount && (
                          <Button size="icon" variant="ghost" className="h-7 w-7 shrink-0" onClick={() => setRevealedFields(prev => { const n = { ...prev }; delete n.bankAccount; return n; })} data-testid="button-hide-bank-account">
                            <EyeOff className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <ProfileField icon={CreditCard} label="Routing Number" value={revealedFields.bankRouting || viewProfile.bankRoutingNumber || "Not provided"} />
                        {viewProfile.bankRoutingNumber && !revealedFields.bankRouting && (
                          <Button size="icon" variant="ghost" className="h-7 w-7 shrink-0" onClick={() => setRevealDialogField("bankRouting")} data-testid="button-reveal-bank-routing">
                            <Eye className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        {revealedFields.bankRouting && (
                          <Button size="icon" variant="ghost" className="h-7 w-7 shrink-0" onClick={() => setRevealedFields(prev => { const n = { ...prev }; delete n.bankRouting; return n; })} data-testid="button-hide-bank-routing">
                            <EyeOff className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                      <ProfileField icon={CreditCard} label="Account Type" value={viewProfile.bankAccountType} className="capitalize" />
                      <ProfileField icon={Mail} label="Zelle Email" value={viewProfile.zelleEmail} />
                      <ProfileField icon={Phone} label="Zelle Phone" value={viewProfile.zellePhone} />
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-4">Bank information not submitted yet.</p>
                  )}
                </TabsContent>
              </Tabs>

              <div className="mt-4 pt-4 border-t">
                <Button variant="outline" size="sm" onClick={() => setShowAuditLog(!showAuditLog)} data-testid="button-toggle-audit-log">
                  <ClipboardList className="h-4 w-4 mr-1" />
                  {showAuditLog ? "Hide" : "View"} Account History
                </Button>
                {showAuditLog && (
                  <div className="mt-3 max-h-56 overflow-y-auto space-y-1.5">
                    {auditLogs.length === 0 ? (
                      <p className="text-xs text-muted-foreground text-center py-2">No account history recorded yet.</p>
                    ) : (
                      auditLogs.map((log: any) => (
                        <div key={log.id} className="text-xs flex gap-2 items-start" data-testid={`audit-log-${log.id}`}>
                          <div className="w-1 rounded-full bg-border shrink-0 mt-1" />
                          <div className="flex-1 min-w-0">
                            <span className="font-medium">{log.actorName || "System"}</span>
                            <span className="text-muted-foreground mx-1">·</span>
                            <span className="capitalize">{log.action.replace(/_/g, " ")}</span>
                            {log.metadata && typeof log.metadata === "object" && Object.entries(log.metadata).map(([k, v]: any) => (
                              typeof v === "object" && v?.from !== undefined ? (
                                <span key={k} className="text-muted-foreground ml-1">
                                  ({k.replace(/_/g, " ")}: <span className="line-through">{String(v.from ?? "—")}</span> → {String(v.to ?? "—")})
                                </span>
                              ) : k === "passwordChanged" ? (
                                <span key={k} className="text-amber-600 ml-1">• password changed</span>
                              ) : null
                            ))}
                          </div>
                          <span className="text-muted-foreground whitespace-nowrap shrink-0">
                            {log.createdAt ? new Date(log.createdAt).toLocaleDateString() : ""}
                          </span>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </>)}
          </DialogContent>
        </Dialog>

        <Dialog open={revealDialogField !== null} onOpenChange={(open) => { if (!open) { setRevealDialogField(null); setRevealReason(""); } }}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Access Sensitive Data</DialogTitle>
              <DialogDescription>
                You are requesting access to encrypted {revealDialogField === "ssn" ? "SSN" : revealDialogField === "ein" ? "EIN" : revealDialogField === "bankAccount" ? "bank account number" : "routing number"} data. This action will be permanently logged for compliance.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Reason for access *</Label>
                <Textarea
                  placeholder="Enter business reason for accessing this data..."
                  value={revealReason}
                  onChange={(e) => setRevealReason(e.target.value)}
                  data-testid="input-reveal-reason"
                />
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => { setRevealDialogField(null); setRevealReason(""); }} data-testid="button-cancel-reveal">Cancel</Button>
                <Button
                  variant="destructive"
                  disabled={revealReason.length < 3 || revealMutation.isPending}
                  onClick={() => {
                    if (profileViewUserId && revealDialogField) {
                      revealMutation.mutate({ userId: profileViewUserId, field: revealDialogField, reason: revealReason });
                    }
                  }}
                  data-testid="button-confirm-reveal"
                >
                  {revealMutation.isPending ? "Revealing..." : "Reveal Data"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        <AlertDialog open={deleteUserId !== null} onOpenChange={(open) => { if (!open) setDeleteUserId(null); }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Remove Team Member</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to remove {deleteUsername}? This will permanently delete their account and all associated data.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="button-cancel-delete">Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} data-testid="button-confirm-delete">Remove</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>


        <Dialog
          open={!!pmRulesUserId}
          onOpenChange={(open) => {
            if (!open) setPmRulesUserId(null);
          }}
        >
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ClipboardEdit className="w-5 h-5" />
                Rules & Settings — {pmRulesUserName}
              </DialogTitle>
              <DialogDescription>
                Default rules that apply to all units managed by this Property Manager. Individual units can override these settings.
              </DialogDescription>
            </DialogHeader>
            <PmRulesForm
              initialData={pmRulesData ?? null}
              form={pmRulesForm}
              setForm={setPmRulesForm}
              onSave={() => {
                if (pmRulesUserId) {
                  const rules: any = {};
                  if (pmRulesForm.defaultCheckoutTime) rules.defaultCheckoutTime = pmRulesForm.defaultCheckoutTime;
                  if (pmRulesForm.cleaningNotes) rules.cleaningNotes = pmRulesForm.cleaningNotes;
                  rules.notifyOnCheckout = pmRulesForm.notifyOnCheckout;
                  rules.notifyOnCleaningComplete = pmRulesForm.notifyOnCleaningComplete;
                  rules.requireFobReturn = pmRulesForm.requireFobReturn;
                  if (pmRulesForm.defaultPriority) rules.defaultPriority = pmRulesForm.defaultPriority;
                  if (pmRulesForm.additionalNotes) rules.additionalNotes = pmRulesForm.additionalNotes;
                  savePmRulesMutation.mutate({ userId: pmRulesUserId, rules });
                }
              }}
              isSaving={savePmRulesMutation.isPending}
            />
            <Collapsible open={contactsOpen} onOpenChange={setContactsOpen} className="border-t pt-4 mt-2 space-y-4">
              <div className="flex items-center justify-between">
                <CollapsibleTrigger asChild>
                  <button type="button" className="flex items-center gap-1.5 text-sm font-medium hover:text-foreground/80 transition-colors" data-testid="trigger-contacts-section">
                    <Users className="w-4 h-4 text-muted-foreground" />
                    Additional Contacts
                    {additionalContacts.length > 0 && (
                      <span className="text-xs text-muted-foreground font-normal">({additionalContacts.length})</span>
                    )}
                    {contactsOpen ? <ChevronUp className="w-3.5 h-3.5 text-muted-foreground" /> : <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />}
                  </button>
                </CollapsibleTrigger>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="text-xs"
                  onClick={() => {
                    const newContact: AdditionalContact = {
                      id: crypto.randomUUID(),
                      name: "",
                      email: "",
                      phone: "",
                      label: "",
                      notify: { invoices: false, reservations: false, cleaning: false, general: false },
                    };
                    setAdditionalContacts(prev => [...prev, newContact]);
                    setContactsOpen(true);
                  }}
                  data-testid="button-add-contact"
                >
                  <Plus className="w-3 h-3 mr-1" /> Add Contact
                </Button>
              </div>
              <CollapsibleContent className="space-y-4">
              <p className="text-xs text-muted-foreground -mt-2">Extra email recipients for automated messages. Choose which types each contact receives.</p>
              {additionalContacts.length === 0 ? (
                <p className="text-xs text-muted-foreground italic py-1">No additional contacts yet. The primary email always receives all notifications.</p>
              ) : (
                <div className="space-y-3">
                  {additionalContacts.map((contact, idx) => (
                    <div key={contact.id} className="border rounded-lg p-3 space-y-2.5 relative bg-muted/20" data-testid={`contact-card-${idx}`}>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="absolute top-2 right-2 h-6 w-6 text-muted-foreground hover:text-destructive"
                        onClick={() => setAdditionalContacts(prev => prev.filter(c => c.id !== contact.id))}
                        data-testid={`button-remove-contact-${idx}`}
                      >
                        <X className="w-3.5 h-3.5" />
                      </Button>
                      <div className="grid grid-cols-2 gap-2 pr-8">
                        <div>
                          <Label className="text-xs text-muted-foreground">Name *</Label>
                          <Input
                            className="h-8 text-sm"
                            placeholder="Jane Smith"
                            value={contact.name}
                            onChange={e => setAdditionalContacts(prev => prev.map(c => c.id === contact.id ? { ...c, name: e.target.value } : c))}
                            data-testid={`input-contact-name-${idx}`}
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-muted-foreground">Label (optional)</Label>
                          <Input
                            className="h-8 text-sm"
                            placeholder="Partner, Accountant..."
                            value={contact.label || ""}
                            onChange={e => setAdditionalContacts(prev => prev.map(c => c.id === contact.id ? { ...c, label: e.target.value } : c))}
                            data-testid={`input-contact-label-${idx}`}
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-muted-foreground">Email *</Label>
                          <Input
                            className="h-8 text-sm"
                            type="email"
                            placeholder="jane@example.com"
                            value={contact.email}
                            onChange={e => setAdditionalContacts(prev => prev.map(c => c.id === contact.id ? { ...c, email: e.target.value } : c))}
                            data-testid={`input-contact-email-${idx}`}
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-muted-foreground">Phone (optional)</Label>
                          <Input
                            className="h-8 text-sm"
                            placeholder="+1 305-555-0100"
                            value={contact.phone || ""}
                            onChange={e => setAdditionalContacts(prev => prev.map(c => c.id === contact.id ? { ...c, phone: e.target.value } : c))}
                            data-testid={`input-contact-phone-${idx}`}
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground mb-1.5 block">Receives</Label>
                        <div className="flex flex-wrap gap-3">
                          {([
                            { key: "invoices", label: "Invoices", Icon: Receipt },
                            { key: "reservations", label: "Reservations", Icon: CalendarClock },
                            { key: "cleaning", label: "Cleaning", Icon: SprayCan },
                            { key: "general", label: "General", Icon: Bell },
                          ] as const).map(({ key, label, Icon }) => (
                            <label key={key} className="flex items-center gap-1.5 text-xs cursor-pointer select-none" data-testid={`checkbox-${key}-${idx}`}>
                              <Checkbox
                                checked={contact.notify[key]}
                                onCheckedChange={checked => setAdditionalContacts(prev => prev.map(c =>
                                  c.id === contact.id ? { ...c, notify: { ...c.notify, [key]: !!checked } } : c
                                ))}
                              />
                              <Icon className="w-3 h-3 text-muted-foreground" />
                              {label}
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <Button
                className="w-full"
                onClick={() => {
                  if (pmRulesUserId) {
                    const invalid = additionalContacts.find(c => !c.name && !c.email);
                    if (invalid) {
                      toast({ title: "Each contact needs at least a name or email.", variant: "destructive" });
                      return;
                    }
                    saveContactsMutation.mutate({ userId: pmRulesUserId, contacts: additionalContacts });
                  }
                }}
                disabled={saveContactsMutation.isPending}
                data-testid="button-save-contacts"
              >
                {saveContactsMutation.isPending ? "Saving..." : "Save Additional Contacts"}
              </Button>
              </CollapsibleContent>
            </Collapsible>
            <div className="border-t pt-4 mt-2">
              <p className="text-xs text-muted-foreground mb-2">Copy current saved PM rules as unit-level overrides to all their apartments (saves first, then propagates).</p>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => pmRulesUserId && applyRulesToAllMutation.mutate(pmRulesUserId)}
                disabled={applyRulesToAllMutation.isPending}
                data-testid="button-apply-rules-to-all"
              >
                {applyRulesToAllMutation.isPending ? "Applying..." : "Apply rules to all apartments"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}

function ProfileField({ icon: Icon, label, value, className }: { icon: any; label: string; value: string | null | undefined; className?: string }) {
  return (
    <div className="space-y-1">
      <p className="text-xs text-muted-foreground flex items-center gap-1">
        <Icon className="w-3 h-3" />
        {label}
      </p>
      <p className={`text-sm font-medium ${className || ""}`} data-testid={`text-profile-${label.toLowerCase().replace(/\s+/g, '-')}`}>
        {value || <span className="text-muted-foreground font-normal">-</span>}
      </p>
    </div>
  );
}

type PmRulesFormData = {
  defaultCheckoutTime: string;
  cleaningNotes: string;
  notifyOnCheckout: boolean;
  notifyOnCleaningComplete: boolean;
  requireFobReturn: boolean;
  defaultPriority: string;
  additionalNotes: string;
};

function PmRulesForm({
  initialData,
  form,
  setForm,
  onSave,
  isSaving,
}: {
  initialData: any;
  form: PmRulesFormData;
  setForm: (f: PmRulesFormData) => void;
  onSave: () => void;
  isSaving: boolean;
}) {
  useEffect(() => {
    if (initialData) {
      setForm({
        defaultCheckoutTime: initialData.defaultCheckoutTime ?? "",
        cleaningNotes: initialData.cleaningNotes ?? "",
        notifyOnCheckout: initialData.notifyOnCheckout ?? false,
        notifyOnCleaningComplete: initialData.notifyOnCleaningComplete ?? false,
        requireFobReturn: initialData.requireFobReturn ?? true,
        defaultPriority: initialData.defaultPriority ?? "normal",
        additionalNotes: initialData.additionalNotes ?? "",
      });
    }
  }, [initialData]);

  return (
    <div className="space-y-5 py-2">
      <div className="space-y-2">
        <Label className="flex items-center gap-1.5 text-sm font-medium">
          <Clock className="w-4 h-4 text-muted-foreground" />
          Default Checkout Time
        </Label>
        <Input
          type="time"
          value={form.defaultCheckoutTime}
          onChange={e => setForm({ ...form, defaultCheckoutTime: e.target.value })}
          placeholder="11:00"
          data-testid="input-pm-checkout-time"
        />
        <p className="text-xs text-muted-foreground">Used as the default checkout time for all units unless overridden per unit.</p>
      </div>

      <div className="space-y-2">
        <Label className="flex items-center gap-1.5 text-sm font-medium">
          <ClipboardEdit className="w-4 h-4 text-muted-foreground" />
          Default Cleaning Notes/Instructions
        </Label>
        <Textarea
          value={form.cleaningNotes}
          onChange={e => setForm({ ...form, cleaningNotes: e.target.value })}
          placeholder="Default instructions for cleaners across all units..."
          rows={3}
          data-testid="input-pm-cleaning-notes"
        />
      </div>

      <div className="space-y-2">
        <Label className="flex items-center gap-1.5 text-sm font-medium">
          <AlertCircle className="w-4 h-4 text-muted-foreground" />
          Default Cleaning Job Priority
        </Label>
        <Select value={form.defaultPriority} onValueChange={v => setForm({ ...form, defaultPriority: v })}>
          <SelectTrigger data-testid="select-pm-priority">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="low">Low</SelectItem>
            <SelectItem value="normal">Normal</SelectItem>
            <SelectItem value="urgent">Urgent</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="border rounded-lg p-4 space-y-3">
        <p className="text-sm font-medium flex items-center gap-1.5">
          <Bell className="w-4 h-4 text-muted-foreground" />
          Notification Preferences
        </p>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm font-normal">Notify on Checkout Submission</Label>
              <p className="text-xs text-muted-foreground">Send notification when a guest submits a checkout</p>
            </div>
            <Switch
              checked={form.notifyOnCheckout}
              onCheckedChange={v => setForm({ ...form, notifyOnCheckout: v })}
              data-testid="switch-pm-notify-checkout"
            />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm font-normal">Notify on Cleaning Complete</Label>
              <p className="text-xs text-muted-foreground">Send notification when a cleaning job is completed</p>
            </div>
            <Switch
              checked={form.notifyOnCleaningComplete}
              onCheckedChange={v => setForm({ ...form, notifyOnCleaningComplete: v })}
              data-testid="switch-pm-notify-cleaning"
            />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm font-normal">Require FOB Return</Label>
              <p className="text-xs text-muted-foreground">Require guests to return the key FOB on checkout</p>
            </div>
            <Switch
              checked={form.requireFobReturn}
              onCheckedChange={v => setForm({ ...form, requireFobReturn: v })}
              data-testid="switch-pm-fob-return"
            />
          </div>
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-sm font-medium">Additional Notes</Label>
        <Textarea
          value={form.additionalNotes}
          onChange={e => setForm({ ...form, additionalNotes: e.target.value })}
          placeholder="Any additional notes or policies for this property manager..."
          rows={3}
          data-testid="input-pm-additional-notes"
        />
      </div>

      <Button className="w-full" onClick={onSave} disabled={isSaving} data-testid="button-save-pm-rules">
        {isSaving ? "Saving..." : "Save Rules & Settings"}
      </Button>
    </div>
  );
}
